import * as AgGrid from "ag-grid-community";
import * as datasource from "grid/datasource";
import * as dom from "utils/dom";
import * as gridUtils from "grid/utils";
import * as http from "utils/http";
import * as notifications from "utils/notifications";
import * as security from "utils/security";
import { EDITABLE_AMOUNT_COL_DEF } from "controllers/allocations/columns";
import { ToggleAllRows } from "grid/toggleAllRows";
import {
  AMOUNT_COL_DEF,
  AMOUNT_FILTER_COL_DEF,
  DATE_FILTER_COL_DEF,
  DEFAULT_COL_DEF,
  ACTIONS_COL_DEF,
  TEXT_FILTER_COL_DEF,
} from "../../grid/columns";
import { BulkAction } from "grid/bulk_action";
import * as rowActions from "grid/row_actions";
import { GridController } from "grid/grid_controller";
import { FeeManager } from "./fee_manager";
import { debounce } from "utils/events";

enum Action {
  EDIT = "edit",
  PROVISION = "provision",
  PAY = "pay",
}

function showEditMode($agCell: HTMLElement) {
  if ($agCell) {
    dom.hideElement($agCell.querySelector<HTMLButtonElement>(`button[data-action="${Action.EDIT}"]`));
    dom.showElement($agCell.querySelector<HTMLButtonElement>(`button[data-action="${Action.PROVISION}"]`));
  }
}

function showNormalMode($agCell: HTMLElement) {
  if ($agCell) {
    dom.hideElement($agCell.querySelector<HTMLButtonElement>(`button[data-action="${Action.PROVISION}"]`));
    dom.showElement($agCell.querySelector<HTMLButtonElement>(`button[data-action="${Action.EDIT}"]`));
  }
}

const GRID_CALLBACKS: Partial<AgGrid.GridOptions> = {
  onRowEditingStarted(params) {
    showEditMode(gridUtils.actionsCell(params));
  },
  onRowEditingStopped(params) {
    showNormalMode(gridUtils.actionsCell(params));
  },
};

interface DuePayment {
  account_number: string;
  company_name: string;
  currency_code: string;
  customer_number: string;
  due_amount: number;
  due_date: string;
  invoice_number: string;
  payment_amount: number;
  state: string;
  zip_code: string;
}

class SelectionManager {
  private customerNumber: String | null = null;

  onRowSelected(params: AgGrid.RowSelectedEvent<DuePayment, any>) {
    this.preventMultipleCustomerNumberSelection(params);
  }

  private preventMultipleCustomerNumberSelection(params: AgGrid.RowSelectedEvent<DuePayment, any>) {
    this.customerNumber ||= params.data.customer_number;

    if (this.customerNumber != params.data.customer_number) {
      params.node.setSelected(false);
    }

    if (params.api.getSelectedRows().length == 0) {
      this.customerNumber = null;
    }
  }
}

export default class extends GridController<DuePayment> {
  static values = Object.assign(super.values, {
    allowedDomains: Array,
    bulkPaymentUrl: String,
    feesUrl: String,
    isSelectionEnabled: Boolean,
  });

  declare readonly allowedDomainsValue: string[];
  declare readonly feesUrlValue: string;
  declare readonly bulkPaymentUrlValue: string;
  declare readonly hasTotalPaymentAmountTarget: boolean;
  declare readonly isSelectionEnabledValue: boolean;

  bulkPayAction?: BulkAction<DuePayment>;
  feeManager?: FeeManager;

  connect() {
    if (this.isSelectionEnabledValue) {
      this.bulkPayAction = new BulkAction(document.querySelector(".pay-selected"));
      this.bulkPayAction.onClick(this.handlePay.bind(this));
    }
    const waiveFeeCheckbox: HTMLInputElement = document.querySelector('[name="waive_convenience_fee"]');
    if (waiveFeeCheckbox) {
      this.feeManager = new FeeManager(waiveFeeCheckbox, this.feesUrlValue);
    }
    super.connect();
  }

  override buildGrid() {
    const ctrl = this;
    const { i18nJson, paginationPageSize } = this.gridTarget.dataset;
    const selectionManager = new SelectionManager();
    const colDefs: AgGrid.ColDef[] = [
      ...(ctrl.isSelectionEnabledValue ? [{ width: 36, checkboxSelection: true, headerComponent: ToggleAllRows }] : []),
      { field: "customer_number", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF, sortable: true },
      { field: "account_number", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF, sortable: true },
      { field: "invoice_number", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF, sortable: true },
      { field: "company_name", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF, sortable: true },
      { field: "due_date", ...DEFAULT_COL_DEF, ...DATE_FILTER_COL_DEF, sortable: true, sort: "asc" },
      { field: "due_amount", ...AMOUNT_COL_DEF, ...AMOUNT_FILTER_COL_DEF },
      { field: "payment_amount", ...EDITABLE_AMOUNT_COL_DEF, sortable: false },
      {
        ...ACTIONS_COL_DEF,
        cellClass: "actions-cell", // TODO: drop
        onCellClicked: (params) => {
          rowActions.onActionCellClicked<Action>(params, async function (action) {
            switch (action) {
              case Action.EDIT:
                return ctrl.handleEdit(params);
              case Action.PROVISION:
                return ctrl.handleProvision(params);
              case Action.PAY:
                return await ctrl.handlePay([params.data]);
            }
          });
        },
      },
    ];

    const dataSource = new datasource.Datasource({ callbacks: {}, limit: paginationPageSize });
    // prevent double-acting with checkbox
    const { maxBlocksInCache, onCellFocused, ...defaultOptions } = gridUtils.DEFAULT_GRID_OPTIONS;

    const options: AgGrid.GridOptions<DuePayment> = {
      ...defaultOptions,
      defaultColDef: {
        ...(gridUtils.DEFAULT_GRID_OPTIONS.defaultColDef as AgGrid.ColDef<DuePayment, any>),
      },
      datasource: dataSource,
      onCellEditingStopped(params) {
        if (params.newValue <= 0 && params.column.colId === "payment_amount") {
          params.node.setDataValue(params.column, params.oldValue);
        }
      },
      editType: "fullRow",
      cacheBlockSize: parseInt(paginationPageSize),
      columnDefs: gridUtils.buildColumnDefs(colDefs, { i18n: JSON.parse(i18nJson) }),
      onRowEditingStarted: GRID_CALLBACKS.onRowEditingStarted,
      onRowEditingStopped: GRID_CALLBACKS.onRowEditingStopped,
      pagination: true,
      paginationPageSize: parseInt(paginationPageSize),
    };

    const debouncedFeeRefresh = debounce(ctrl.handleFeeRefresh.bind(this), 300);
    const selectionOptions: AgGrid.GridOptions<DuePayment> = ctrl.isSelectionEnabledValue
      ? {
          rowSelection: "multiple",
          suppressRowClickSelection: true,
          suppressRowDeselection: true,
          onRowSelected(event) {
            ctrl.bulkPayAction.onRowSelected(event);
            selectionManager.onRowSelected(event);
            if (ctrl.feeManager) {
              debouncedFeeRefresh(ctrl.bulkPayAction.selectedData());
            }
          },
        }
      : {};

    const gridApi = AgGrid.createGrid(this.gridTarget, { ...options, ...selectionOptions });
    dataSource.gridApi = gridApi;
    return gridApi;
  }

  handleEdit(params: AgGrid.CellClickedEvent) {
    params.api.startEditingCell({ rowIndex: params.node.rowIndex, colKey: "payment_amount" });
    showEditMode(gridUtils.actionsCell(params));
  }

  async handlePay(payments: DuePayment[]) {
    const response = await http.httpPost(this.bulkPaymentUrlValue, {
      is_waive_fee: this.feeManager?.isWaiveFee() || false,
      selected_payments: payments.map((payment) => ({
        account_number: payment.account_number,
        company_name: payment.company_name,
        currency_code: payment.currency_code,
        customer_number: payment.customer_number,
        payment_amount: payment.payment_amount,
        state: payment.state,
        zip_code: payment.zip_code,
      })),
    });

    if (response.ok) {
      const data = await response.json();
      security.redirectToAllowedUrl(this.allowedDomainsValue, data.redirect_url);
    } else {
      const data = await response.json();
      notifications.alert(data.error);
    }
  }

  async handleProvision(params: AgGrid.CellClickedEvent) {
    if (!this.gridApi) return;
    this.gridApi.stopEditing();
    showNormalMode(gridUtils.actionsCell(params));
  }

  async handleFeeRefresh(payments: DuePayment[]) {
    const firstPayment = payments[0];
    if (firstPayment) {
      this.feeManager.refreshFee({
        payment_amount: payments.reduce((sum, payment) => sum + payment.payment_amount, 0),
        state: firstPayment.state,
      });
    } else {
      this.feeManager.clearFee();
    }
  }
}
